import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { setLanguage, SUPPORTED_LANGUAGES, isRTL } from '../utils/translations';
import { Globe, ChevronDown, Check } from 'lucide-react';
import { useMarket } from '../context/MarketContext';

const LanguageToggle = () => {
    const market = useMarket();
    const navigate = useNavigate();
    const marketLang = market?.language || 'ar';
    const [currentLang, setCurrentLang] = useState(marketLang);
    const [isOpen, setIsOpen] = useState(false);
    const [dropdownStyle, setDropdownStyle] = useState({});
    
    const wrapperRef = useRef(null);
    const buttonRef = useRef(null);

    useEffect(() => {
        if (market?.language) {
            setCurrentLang(market.language);
        }
    }, [market?.language]);

    useEffect(() => {
        const updateLangState = () => {
            const geoContextStr = localStorage.getItem('luxvono_geo_context');
            if (geoContextStr) {
                try {
                    const ctx = JSON.parse(geoContextStr);
                    if (ctx.language) {
                        setCurrentLang(ctx.language);
                    }
                } catch (e) {
                    console.error("Failed to parse geo context in LanguageToggle", e);
                }
            }
        };

        const handleClickOutside = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        updateLangState();
        window.addEventListener('languageChange', updateLangState);
        window.addEventListener('geoContextChange', updateLangState);
        document.addEventListener('mousedown', handleClickOutside);
        
        return () => {
            window.removeEventListener('languageChange', updateLangState);
            window.removeEventListener('geoContextChange', updateLangState);
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    useEffect(() => {
        if (!isOpen) return;

        const updatePosition = () => {
            if (!wrapperRef.current) return;
            const rect = wrapperRef.current.getBoundingClientRect();
            const viewportWidth = window.innerWidth;
            const dropdownWidth = 230;
            const margin = 12;
            const rtl = isRTL();
            
            const preferredLeftOffset = rtl ? 0 : (rect.width - dropdownWidth);
            const minLeftOffset = margin - rect.left;
            const maxLeftOffset = viewportWidth - rect.left - dropdownWidth - margin;
            
            let finalLeftOffset = preferredLeftOffset;
            if (maxLeftOffset >= minLeftOffset) {
                finalLeftOffset = Math.max(minLeftOffset, Math.min(preferredLeftOffset, maxLeftOffset));
            } else {
                finalLeftOffset = (viewportWidth - dropdownWidth) / 2 - rect.left;
            }
            
            setDropdownStyle({
                position: 'absolute',
                top: 'calc(100% + 8px)',
                left: `${finalLeftOffset}px`,
                width: `${dropdownWidth}px`,
                maxHeight: '380px',
                overflowY: 'auto'
            });
        };

        updatePosition();
        window.addEventListener('resize', updatePosition);
        window.addEventListener('scroll', updatePosition, true);
        
        return () => {
            window.removeEventListener('resize', updatePosition);
            window.removeEventListener('scroll', updatePosition, true);
        };
    }, [isOpen]);

    const handleSwitch = (lang) => {
        setLanguage(lang);
        if (market?.switchMarket) {
            market.switchMarket(market.country, lang, market.currency, navigate);
        }
        setIsOpen(false);
        if (buttonRef.current) {
            buttonRef.current.focus();
        }
    };

    const currentLangObj = SUPPORTED_LANGUAGES.find(l => l.code === currentLang) || SUPPORTED_LANGUAGES[0];
    const rtl = isRTL();
    
    return (
        <div 
            ref={wrapperRef} 
            className="lux-lang-toggle-wrapper" 
            style={{ position: 'relative', display: 'inline-block', zIndex: 9999 }}
        >
            <style>{`
                button.lux-lang-btn {
                    display: flex !important;
                    align-items: center !important;
                    gap: 7px !important;
                    padding: 6px 13px !important;
                    border-radius: 9999px !important;
                    background: rgba(255, 255, 255, 0.65) !important;
                    border: 1px solid rgba(197, 168, 128, 0.3) !important;
                    cursor: pointer !important;
                    transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1) !important;
                    outline: none !important;
                    color: #1d1d1f !important;
                    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02) !important;
                    line-height: normal !important;
                    white-space: nowrap !important;
                }
                body.theme-dark button.lux-lang-btn {
                    background: rgba(18, 24, 38, 0.75) !important;
                    border-color: rgba(197, 168, 128, 0.25) !important;
                    color: #f5f5f7 !important;
                }
                button.lux-lang-btn:hover {
                    border-color: rgba(197, 168, 128, 0.6) !important;
                    background: rgba(255, 255, 255, 0.95) !important;
                    transform: translateY(-1px);
                }
                body.theme-dark button.lux-lang-btn:hover {
                    background: rgba(28, 36, 56, 0.9) !important;
                    border-color: rgba(197, 168, 128, 0.5) !important;
                }
                .lux-lang-dropdown {
                    background: rgba(255, 255, 255, 0.98);
                    backdrop-filter: blur(20px);
                    -webkit-backdrop-filter: blur(20px);
                    border: 1px solid rgba(197, 168, 128, 0.3);
                    border-radius: 16px;
                    padding: 6px;
                    box-shadow: 0 16px 36px rgba(0, 0, 0, 0.15);
                    display: none;
                    flex-direction: column;
                    gap: 3px;
                    z-index: 10000;
                }
                .lux-lang-dropdown.active {
                    display: flex;
                    animation: luxDropFade 0.25s cubic-bezier(0.16, 1, 0.3, 1);
                }
                @keyframes luxDropFade {
                    from { opacity: 0; transform: translateY(-8px) scale(0.97); }
                    to { opacity: 1; transform: translateY(0) scale(1); }
                }
                body.theme-dark .lux-lang-dropdown {
                    background: rgba(12, 17, 29, 0.96);
                    border-color: rgba(197, 168, 128, 0.25);
                    box-shadow: 0 20px 48px rgba(0, 0, 0, 0.6);
                }
                .lux-lang-item {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 8px 12px;
                    border: none;
                    background: transparent;
                    border-radius: 10px;
                    cursor: pointer;
                    width: 100%;
                    transition: all 0.2s ease;
                    color: #4b5563;
                    outline: none;
                    box-sizing: border-box;
                }
                body.theme-dark .lux-lang-item {
                    color: #94a3b8;
                }
                .lux-lang-item:hover {
                    background: rgba(197, 168, 128, 0.12);
                    color: #C5A880;
                }
                .lux-lang-item.active {
                    background: rgba(197, 168, 128, 0.18);
                    color: #C5A880;
                    font-weight: 700;
                }
                .lux-lang-item-content {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                }
                .lux-lang-flag {
                    font-size: 14px;
                }
                .lux-lang-item-label {
                    font-size: 13px;
                    font-weight: 600;
                }
                .lux-lang-check {
                    color: #C5A880;
                }
            `}</style>

            <button 
                ref={buttonRef}
                onClick={() => setIsOpen(!isOpen)} 
                className="lux-lang-btn"
                aria-label="Select Language"
                aria-haspopup="listbox"
                aria-expanded={isOpen}
            >
                <span className="lux-lang-flag">{currentLangObj.flag}</span>
                <span className="lux-lang-btn-text">{currentLangObj.nativeName}</span>
                <ChevronDown size={11} style={{ 
                    transition: 'transform 0.3s ease',
                    transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                    color: '#C5A880',
                    opacity: 0.8 
                }} />
            </button>
            
            <div 
                className={`lux-lang-dropdown ${isOpen ? 'active' : ''}`} 
                style={dropdownStyle} 
                role="listbox"
            >
                {SUPPORTED_LANGUAGES.map((lang) => {
                    const isActive = currentLang === lang.code;
                    return (
                        <button 
                            key={lang.code}
                            onClick={() => handleSwitch(lang.code)}
                            className={`lux-lang-item ${isActive ? 'active' : ''}`}
                            role="option"
                            aria-selected={isActive}
                        >
                            <div className="lux-lang-item-content">
                                <span className="lux-lang-flag">{lang.flag}</span>
                                <span className="lux-lang-item-label">{lang.nativeName} ({lang.name})</span>
                            </div>
                            {isActive && <Check size={14} className="lux-lang-check" strokeWidth={2.5} />}
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

export default LanguageToggle;
